import { useId } from 'react';
import {
  CheckIcon,
  CircleDashedIcon,
  ShieldSlashIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { buttonVariants } from '@voll-intelligence/ui-kit/ui/button';
import { cn } from '@voll-intelligence/ui-kit/utils';
import { fmtMoneyWhole } from '../results/flight_format.js';
import type { BenefitState } from './fare_benefits.js';
import { attributesDeclared, benefitStates, recommendedReasonFor } from './fare_ladder.js';
import { FARE_DIMENSIONS, type FareDimension, type FlightFareSource } from './fare_source.js';
import type { FaresText, FlightFaresCopy } from './fares_copy.js';
import { SupplierNotes } from './supplier_notes.js';

/**
 * A família de tarifa como CARD, e não como degrau de escada (#2262).
 *
 * A escada de chips que a expansão inline usava antes respondia "quanto custa
 * subir": ordenava por preço, imprimia o acréscimo e abreviava o degrau de cima
 * em "tudo da Classic + assento". Esta superfície responde outra pergunta — a
 * do relato que abriu o issue ("I was looking for a comparative between
 * Economy, Economy business and Business"): as famílias ficam LADO A LADO e
 * cada uma escreve a lista inteira de benefícios, para a comparação ser
 * horizontal, linha por linha. Por isso aqui não há `gains`/`inherits`: o que
 * uma família omite, a vizinha na mesma altura tem escrito.
 *
 * Irmão e não um modo da escada, pela mesma razão que `FlightLegDense` é irmã da
 * `FlightLeg`: as duas trocam de ÁRVORE, não de estilo.
 */

/** Glifo e tom por estado da dimensão. Os três tons saem de token do DS — e é
 *  para o âmbar que isto importa: o issue pedia criar um "token warning que não
 *  existe", mas `--v2-tone-amber` (→ `--warning-border`) já É o #c6832b do frame,
 *  como `--v2-tone-green` é o #527a59 e `--v2-tone-red` o #971133. Cor nova aqui
 *  seria um quarto amarelo no sistema. */
const STATE_GLYPH = {
  included: CheckIcon,
  fee: WarningIcon,
  absent: XIcon,
  unknown: CircleDashedIcon,
} as const;

/** A frase da dimensão no estado em que ela está. `fee` embrulha a AFIRMATIVA
 *  ("Assento com taxa") porque o benefício existe — quem paga é a pessoa; a
 *  negação, essa, nunca sai do texto (um ✗ vermelho ao lado de "Assento" seria
 *  lido como "tem assento" por quem não decodificou a cor). `unknown` embrulha
 *  a afirmativa pela mesma razão: o que o fornecedor calou vira "· não
 *  informado", nunca um "sem". */
function benefitLabel(
  dim: FareDimension,
  state: BenefitState,
  copy: FlightFaresCopy,
  fare: FlightFareSource,
) {
  const row = copy.rows[dim];
  if (state === 'absent') return row.no;
  if (state === 'fee') return copy.fee(row.yes);
  if (state === 'unknown') return copy.unknown(row.yes);
  const allowance = allowanceOf(dim, fare);
  return allowance ? copy.allowance(row.yes, allowance.pieces, allowance.weightKg) : row.yes;
}

/** A franquia que a tarifa declara para a mão ou o despacho (#6542), só com número: sem
 *  ele a linha fica na frase da dimensão, como sempre. Só na linha incluída — com taxa, o
 *  volume é o que se paga, não o que a tarifa dá. */
function allowanceOf(dim: FareDimension, fare: FlightFareSource) {
  if (dim !== 'carry' && dim !== 'checked') return undefined;
  const line = fare.baggageAllowance?.[dim];
  return line && (line.pieces !== null || line.weightKg !== null) ? line : undefined;
}

/** As CINCO dimensões, sempre: a comparação horizontal da página depende de
 *  todas as colunas terem as mesmas linhas, e "não informado" é uma linha — o
 *  que o provider não declarou nunca vira "sem despacho", que seria erro de
 *  dinheiro, mas também não pode sumir da coluna e desalinhar a comparação. */
function declared(
  fare: FlightFareSource,
  text: FaresText,
): { dim: FareDimension; state: BenefitState }[] {
  const states = benefitStates(fare, text.classify);
  return FARE_DIMENSIONS.map((dim) => ({ dim, state: states[dim] }));
}

/** O motivo da família fora da política, quando nomeável (#5797): sem ele o escudo
 *  seria a única pista, e o "por quê" só apareceria no checkout, junto da
 *  justificativa. */
function policyReasonOf(fare: FlightFareSource, copy: FlightFaresCopy): string | undefined {
  if (fare.withinPolicy !== false) return undefined;
  return copy.policyReason(fare.policyViolations ?? []);
}

/** A linha de benefício no tom de ausência: o glifo vermelho, o texto no peso das
 *  outras (a régua de tom de `.ffs-benefit`). */
function PolicyReasonRow({ reason }: Readonly<{ reason: string | undefined }>) {
  if (!reason) return null;
  return (
    <span className="ffs-benefit is-absent">
      <ShieldSlashIcon size={16} aria-hidden />
      {reason}
    </span>
  );
}

/** A legenda por `attributes.source` — diz de QUEM é a voz do card. */
export function captionOf(source: 'wooba' | 'voll', copy: FlightFaresCopy): string {
  return source === 'voll' ? copy.curatedRules : copy.supplierNotes;
}

export function FareFamilyCard({
  fare,
  selected,
  recommended,
  roving,
  readOnly,
  caption = true,
  onPick,
  text,
}: {
  readonly fare: FlightFareSource;
  readonly selected: boolean;
  readonly recommended: boolean;
  /** Este card carrega o ponto de tabulação do grupo (o escolhido, ou o primeiro
   *  enquanto nada foi escolhido). Quem decide é o trilho — ele é quem conhece a
   *  fila; ver `FareRail`. */
  readonly roving: boolean;
  readonly readOnly?: boolean;
  /** `false` quando o TRILHO já disse de quem é a voz uma vez por todos os
   *  cards (`FareRail`, quando todos declaram a mesma fonte): o card não repete
   *  a legenda sob si. Só a legenda cala — o `SupplierNotes` de um card sem
   *  atributos continua, porque aí a voz é dele. */
  readonly caption?: boolean;
  readonly onPick: () => void;
  readonly text: FaresText;
}) {
  const { copy } = text;
  const nameId = useId();
  const detailId = useId();
  const badgeId = useId();
  // `withinPolicy` é `nullish` no schema: NULO é "não avaliado", e só o `false`
  // explícito acusa. Um escudo por ausência de dado marcaria de vermelho toda
  // sessão sem política configurada.
  const outOfPolicy = fare.withinPolicy === false;
  const policyReason = policyReasonOf(fare, copy);
  const source = fare.attributes?.source;
  // POR QUE esta é a recomendada. A régua é a mesma da escada e mora num lugar
  // só (`recommendedReasonFor`): o trilho escolhe o card, o card diz o motivo.
  const why = recommended
    ? copy.recommendedWhy[
        recommendedReasonFor(fare, text.classify) === 'checked_included' ? 'checked' : 'cheapest'
      ]
    : undefined;
  return (
    // A COLUNA do trilho: o card em cima, a divulgação do texto cru embaixo —
    // irmã do botão, nunca filha dele. Descendente de `role="radio"` é conteúdo
    // apresentacional (some da árvore de acessibilidade), e um `<details>` ali
    // dentro é `nested-interactive` no axe, que a story roda como ERRO. A
    // largura, o snap e a calha do trilho moram aqui agora; o card só preenche.
    <div className="ffs-item">
      {/* O card INTEIRO é o radio: alvo grande, um gesto só. O "Selecionar" /
          "Selecionado" no pé (frame de 29/09, #3875 — o de 31/08 o tinha
          trocado pelo ⦿ ao lado do nome) é o ESTADO do radio desenhado como
          botão, não um segundo controle: botão dentro de botão é DOM inválido,
          e o `aria-checked` já diz o que ele diz.

          `data-fare` é o alvo estável do foco por setas — o trilho move o foco
          pelo DOM, e o nome acessível não serve de âncora (ele é só a família,
          e duas pernas podem oferecer a MESMA). Mesma mecânica do `data-date`
          da grade do `PlannerForm`. O `tabIndex` é o roving do grupo: um ponto
          de tabulação só.

          NOME = a família, e nada mais (`aria-labelledby`); selos, benefícios e
          preço vão na DESCRIÇÃO. Sem isso o nome do radio seria a colagem das
          seis linhas do card, e "Light" — o dado que distingue uma opção da
          vizinha — ficava enterrado no meio dela. */}
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-labelledby={nameId}
        // Os SELOS entram na descrição junto com benefícios e preço: eles ficam na
        // zona morta da borda (fora do `.ffs-detail`), e sem isto "Recomendada" e
        // o escudo de política — que são visuais puros — não chegariam a quem não
        // vê o card.
        aria-describedby={`${badgeId} ${detailId}`}
        className={`ffs-card${selected ? ' is-sel' : ''}`}
        data-fare={fare.fareId}
        tabIndex={roving ? 0 : -1}
        disabled={readOnly}
        onClick={onPick}
      >
        {/* Os dois selos moram na zona morta da borda (sangram -11px), mesma
          gramática do selo da escada — ao lado do nome roubariam a largura de
          180px que a linha de benefício precisa para não quebrar. */}
        <span id={badgeId} className="ffs-detail">
          {/* Os dois selos são o MESMO átomo do DS (`badge [1.0]` no frame, nas duas
            configurações), e não dois spans pintados à mão: o `h-5` do Badge já
            são os 20px de altura do desenho, e o `rounded-4xl` (26px aqui) é
            pill em qualquer coisa dessa altura. As classes `.ffs-*` sobraram só
            para o sangramento — POSIÇÃO é da tela, forma é do átomo.
            O custo medido da padronização está no `px-2` do átomo: "Recomendada"
            passou de 88,7 para 98,4px, contra 90 no frame. Selo flutuante em
            zona morta, então nada se desloca — mas é largura, não é de graça. */}
          {recommended && (
            <>
              {/* `bg-muted-foreground text-background`: a tinta do selo Recomendado
                em todas as superfícies (#3874) — o `default` do átomo é o ink
                cheio (`--primary`), que o design aposentou para este selo. */}
              <Badge className="ffs-tag bg-muted-foreground text-background" title={why}>
                {copy.recommended}
              </Badge>
              {/* O motivo sem mouse: este span já está na DESCRIÇÃO do card
                (`aria-describedby` aponta para o embrulho dos selos), então ele
                não toca o nome acessível — que é só a família. */}
              <span className="ffs-sr">{why}</span>
            </>
          )}
          {outOfPolicy && (
            // `solid`/`danger` e não `destructive`: a variante `destructive` do
            // Badge é fundo a 10%, e o frame pede o vermelho CHEIO. A largura de
            // 30px do desenho sai do átomo (px-2 + o glifo de 12px do
            // `[&>svg]:size-3!`), então o card não a fixa mais.
            <Badge variant="solid" color="danger" className="ffs-shield">
              <ShieldSlashIcon aria-hidden />
              {/* Só o escudo, por decisão do frame: a frase inteira já está na
                linha de política do topo da página, e repeti-la por card
                custaria duas linhas em cada um. Para leitor de tela ela volta —
                lá não existe "o topo da página". */}
              {/* Com o motivo visível, a linha já diz "Fora da política": o `hidden`
                  tira a frase da descrição, que senão a repetiria. */}
              <span className="ffs-sr" hidden={policyReason !== undefined}>
                {copy.offer.outPolicy}
              </span>
            </Badge>
          )}
        </span>
        <span className="ffs-head">
          <span id={nameId} className="ffs-name">
            {fare.name}
          </span>
        </span>
        {/* `display: contents` no embrulho da descrição: ele existe pro
          `aria-describedby` apontar para UM nó, e não pode ganhar caixa — o
          card é um grid, e uma caixa a mais colapsaria os dois vãos em um. */}
        <span id={detailId} className="ffs-detail">
          <span className="ffs-benefits">
            {declared(fare, text).map(({ dim, state }) => {
              const Glyph = STATE_GLYPH[state];
              return (
                <span key={dim} className={`ffs-benefit is-${state}`}>
                  <Glyph size={16} aria-hidden />
                  {benefitLabel(dim, state, copy, fare)}
                </span>
              );
            })}
            <PolicyReasonRow reason={policyReason} />
          </span>
          <span className="ffs-price">
            <span className="ffs-amount">{fmtMoneyWhole(fare.price)}</span>
            {/* Por VIAJANTE, e a distinção é dinheiro: o total de N pessoas se
              resolve na emissão (N vouchers), então o número do card nunca é o
              que sai do cartão quando a viagem tem mais de uma pessoa. */}
            <span className="ffs-per">{copy.perTraveler}</span>
          </span>
        </span>
        {/* O `VOLL/Button` do frame (`default` cheio quando escolhido, `outline`
            quando não), como PINTURA: `aria-hidden` porque o estado já está no
            `aria-checked` do radio, e o texto repetido viraria "Selecionado
            Selecionado" no leitor de tela. O `active:` do átomo não dispara
            num span, e é o que se quer — quem afunda é o card. */}
        <span
          aria-hidden
          className={cn(
            // `sm` (28px): o degrau do "Selecionar" da linha de oferta
            // (`.fq-fares-cta`) — o estado do card é um degrau que ABRE, mais
            // baixo que o CTA que fecha a compra (validação de 30/09).
            buttonVariants({ variant: selected ? 'default' : 'outline', size: 'sm' }),
            'ffs-pick w-full',
          )}
        >
          {selected && <CheckIcon aria-hidden />}
          {selected ? copy.selected : copy.select}
        </span>
      </button>
      {/* UMA VOZ por card: quando ALGUÉM declarou (`attributesDeclared` — o
          fornecedor tipou a família no tariff (`details[]`), com ou sem o mapa
          curado da VOLL por cima, e pelo menos uma das cinco dimensões não é
          `unknown`), as cinco linhas já são dele, e o texto cru do
          consolidador (`SupplierNotes`) sai da tela — mostrar as duas juntas
          seria duas vozes discordando na frente do viajante. A legenda segue
          `attributes.source` (`CAPTION_BY_SOURCE`): fornecedor, ou fornecedor
          completado pelo mapa curado da VOLL — ela diz de QUEM é a voz.
          `attributes` com as cinco `unknown` não conta como fala (o servidor
          já não anexa esse caso, mas o card não confia nele para decidir a
          legenda). Irmã do radio pela mesma razão do `SupplierNotes` que ela
          substitui: descendente de `role="radio"` é apresentacional, e o
          clique não tem para onde borbulhar fora do botão. */}
      {source && attributesDeclared(fare) ? (
        caption && <p className="ffs-source">{captionOf(source, copy)}</p>
      ) : (
        <SupplierNotes benefits={fare.benefits} summary={copy.supplierNotes} />
      )}
    </div>
  );
}
